import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Briefcase, Loader2, Search, Home, Phone, Power, Settings2, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { BatchBar } from "@/components/BatchBar";
import { toast } from "sonner";
import { useCustomFields, CustomFieldManager } from "@/components/CustomFields";
import { usePoll } from "@/hooks/usePoll";
import { confirm } from "@/components/ConfirmDialog";

export default function Clients() {
  const { t } = useI18n();
  const { can } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", email: "", notes: "" });
  const [includeInactive, setIncludeInactive] = useState(false);
  const [cfMgr, setCfMgr] = useState(false);
  const { reload: reloadDefs } = useCustomFields("client");
  const [sel, setSel] = useState({});
  const selIds = Object.keys(sel).filter((k) => sel[k]);

  const batch = async (action) => {
    if (!selIds.length) return;
    if (!(await confirm({ title: `${action} clients`, description: `${action} ${selIds.length} client(s)?`, confirmText: action, destructive: action === "delete" || action === "deactivate" }))) return;
    try {
      const { data } = await client.post("/clients/batch", { ids: selIds, action });
      const reasons = [...new Set((data.skipped || []).map((s) => s.reason).filter(Boolean))].join("; ");
      if (data.done.length) toast.success(`${data.done.length} done${data.skipped.length ? ` · ${data.skipped.length} skipped (${reasons})` : ""}.`);
      else toast.warning(`Nothing deleted — ${data.skipped.length} skipped${reasons ? `: ${reasons}` : ""}.`);
      setSel({}); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const load = () => client.get(`/clients?includeInactive=${includeInactive}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [includeInactive]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(load, 15000);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = !s ? rows : rows.filter((c) => [c.name, c.contact, c.notes].filter(Boolean).some((v) => v.toLowerCase().includes(s)));
    return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [rows, q]);
  const allSelected = filtered.length > 0 && filtered.every((c) => sel[c.id]);
  const toggleAll = (v) => { const next = { ...sel }; filtered.forEach((c) => { next[c.id] = !!v; }); setSel(next); };

  const create = async () => {
    setBusy(true);
    try { await client.post("/clients", form); toast.success(t("toast.clientSaved", "Client saved")); setOpen(false); setForm({ name: "", contact: "", email: "", notes: "" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="clients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-ink">{t("nav.clients", "Clients")}</h2>
          <p className="text-sm text-slate-500">{t("cli.subtitle", "Clients and the properties you service.")}</p>
        </div>
        {can("client.manage") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="client-create-button" className="bg-brand hover:bg-brand-dark"><Plus className="h-4 w-4 mr-2" />{t("cli.new", "New client")}</Button></DialogTrigger>
            <DialogContent data-testid="client-create-dialog">
              <DialogHeader><DialogTitle>{t("cli.new", "New client")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>{t("common.name", "Name")}</Label><Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("cli.contact", "Contact")}</Label><Input data-testid="client-contact-input" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("cli.email", "Email (for job updates)")}</Label><Input data-testid="client-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.com" className="mt-1.5" /></div>
                <div><Label>{t("common.notes", "Notes")}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel", "Cancel")}</Button>
                <Button data-testid="client-save-btn" onClick={create} disabled={busy || !form.name} className="bg-brand hover:bg-brand-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.create", "Create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("cli.search", "Search clients by name or contact…")} className="pl-9" />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="clients-include-inactive" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          {t("cli.includeInactive", "Include inactive")}
        </label>
        {can("client.manage") && <Button variant="outline" size="sm" data-testid="clients-manage-fields" onClick={() => setCfMgr(true)}><Settings2 className="h-3.5 w-3.5 mr-1.5" />{t("cf.manage", "Custom fields")}</Button>}
      </div>

      {can("client.manage") && selIds.length > 0 && (
        <BatchBar count={selIds.length} onClear={() => setSel({})} actions={[
          { key: "deactivate", label: "Deactivate", icon: <Power className="h-4 w-4 mr-1.5" />, className: "text-amber-600 border-amber-200", onClick: () => batch("deactivate") },
          { key: "reactivate", label: "Reactivate", icon: <Power className="h-4 w-4 mr-1.5" />, onClick: () => batch("reactivate") },
          { key: "delete", label: "Delete", icon: <Trash2 className="h-4 w-4 mr-1.5" />, className: "text-red-600 border-red-200", onClick: () => batch("delete") },
        ]} />
      )}

      <Card className="border-slate-200">
        <Table>
          <TableHeader><TableRow>
            {can("client.manage") && <TableHead className="w-8"><Checkbox data-testid="clients-select-all" checked={allSelected} onCheckedChange={toggleAll} /></TableHead>}
            <TableHead>{t("common.name", "Name")}</TableHead>
            <TableHead>{t("cli.contact", "Contact")}</TableHead>
            <TableHead className="text-center">{t("cli.properties", "Properties")}</TableHead>
            <TableHead>{t("common.status", "Status")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10"><Briefcase className="h-7 w-7 mx-auto mb-1" />{q ? t("common.noResults", "No matches") : "—"}</TableCell></TableRow> :
              filtered.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/clients/${c.id}`)}>
                  {can("client.manage") && <TableCell className="py-2" onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`client-select-${c.id}`} checked={!!sel[c.id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [c.id]: !!v }))} /></TableCell>}
                  <TableCell className="font-medium text-slate-900">{c.name}</TableCell>
                  <TableCell className="text-slate-600">{c.contact ? <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.contact}</span> : "—"}</TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className="bg-slate-50"><Home className="h-3 w-3 mr-1" />{c.propertyCount ?? 0}</Badge></TableCell>
                  <TableCell>{c.status === "deactivated" ? <Badge variant="outline" className="bg-slate-100 text-slate-500">{t("staff.inactive", "Inactive")}</Badge> : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{t("st.active", "Active")}</Badge>}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <CustomFieldManager entity="client" open={cfMgr} onOpenChange={setCfMgr} onChanged={reloadDefs} />
    </div>
  );
}
